import { Button, ButtonGroup, ButtonLink } from "@sikt/sds-button";
import { Heading1, Paragraph } from "@sikt/sds-core";
import { Fieldset } from "@sikt/sds-form";
import { AddIcon, DeleteIcon } from "@sikt/sds-icons";
import { NumberInput, TextInput } from "@sikt/sds-input";
import { Alert, GuidePanel } from "@sikt/sds-message";
import { RadioFieldset, RadioInput } from "@sikt/sds-radio";
import { useState, type ChangeEvent, type FormEvent } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  DOTS_MAX,
  DOTS_MIN,
  OPTIONS_MAX,
  OPTIONS_MIN,
  OPTION_MAX,
  TITLE_MAX,
} from "../../shared/limits";
import { createVoteSchema } from "../../shared/schemas";
import type { VoteMode } from "../../shared/types";
import { CopyField } from "../components/CopyField";
import { ApiError, api } from "../lib/api";
import { rememberVote } from "../lib/myVotes";
import styles from "./CreatePage.module.css";

interface Created {
  voteId: string;
  resultsId: string;
  title: string;
}

export function CreatePage() {
  const [title, setTitle] = useState("");
  const [options, setOptions] = useState<string[]>(["", ""]);
  const [mode, setMode] = useState<VoteMode>("single");
  const [maxDots, setMaxDots] = useState("3");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<Created | null>(null);

  const clearError = (key: string) => {
    setFieldErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const setOption = (index: number, value: string) => {
    setOptions((prev) => prev.map((o, i) => (i === index ? value : o)));
    clearError(`options.${index}`);
  };

  const addOption = () => {
    setOptions((prev) =>
      prev.length < OPTIONS_MAX ? [...prev, ""] : prev,
    );
  };

  const removeOption = (index: number) => {
    setOptions((prev) =>
      prev.length > OPTIONS_MIN ? prev.filter((_, i) => i !== index) : prev,
    );
    setFieldErrors({});
  };

  const handleMode = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === "single" || value === "multiple" || value === "dots") {
      setMode(value);
      clearError("maxDots");
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setServerError(null);

    const payload = {
      title,
      options,
      mode,
      ...(mode === "dots"
        ? { maxDots: Number.parseInt(maxDots, 10) }
        : {}),
    };
    const parsed = createVoteSchema.safeParse(payload);
    if (!parsed.success) {
      const errors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path.join(".");
        if (!(key in errors)) errors[key] = issue.message;
      }
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setBusy(true);
    try {
      const response = await api.createVote(parsed.data);
      rememberVote({
        voteId: response.voteId,
        resultsId: response.resultsId,
        title: parsed.data.title,
        createdAt: new Date().toISOString(),
      });
      setCreated({ ...response, title: parsed.data.title });
      window.scrollTo({ top: 0 });
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.details) {
          const errors: Record<string, string> = {};
          for (const detail of error.details) {
            if (!(detail.path in errors)) errors[detail.path] = detail.message;
          }
          setFieldErrors(errors);
        }
        setServerError(error.message);
      } else {
        setServerError("Noe gikk galt. Prøv igjen.");
      }
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setCreated(null);
    setTitle("");
    setOptions(["", ""]);
    setMode("single");
    setMaxDots("3");
    setFieldErrors({});
    setServerError(null);
  };

  if (created) {
    const origin = window.location.origin;
    return (
      <div className={styles.panel}>
        <Heading1 size="l">Avstemningen er klar</Heading1>
        <Alert variant="success">
          <Paragraph>«{created.title}» er opprettet.</Paragraph>
        </Alert>
        <CopyField
          label="Stemmelenke – del med deltakerne"
          value={`${origin}/v/${created.voteId}`}
        />
        <CopyField
          label="Resultatlenke – hold den for deg selv"
          value={`${origin}/r/${created.resultsId}`}
        />
        <GuidePanel variant="info">
          <Paragraph>
            Alle med stemmelenken kan svare. Resultatlenken gir tilgang til
            live-visningen og kan ikke gjettes ut fra stemmelenken. Du finner
            begge igjen under «Dine avstemninger» på forsiden i denne
            nettleseren.
          </Paragraph>
        </GuidePanel>
        <ButtonGroup variant="left">
          <ButtonLink asChild variant="strong">
            <RouterLink to={`/r/${created.resultsId}`}>
              Åpne resultatvisningen
            </RouterLink>
          </ButtonLink>
          <ButtonLink asChild>
            <RouterLink to={`/v/${created.voteId}`}>
              Til stemmesiden
            </RouterLink>
          </ButtonLink>
          <Button type="button" variant="transparent" onClick={reset}>
            Lag en ny avstemning
          </Button>
        </ButtonGroup>
      </div>
    );
  }

  return (
    <form className={styles.panel} onSubmit={handleSubmit} noValidate>
      <Heading1 size="l">Opprett avstemning</Heading1>
      {serverError && (
        <Alert variant="critical">
          <Paragraph>{serverError}</Paragraph>
        </Alert>
      )}

      <TextInput
        label="Spørsmål"
        helpText={`Maks ${TITLE_MAX} tegn.`}
        value={title}
        maxLength={TITLE_MAX}
        onChange={(_, value) => {
          setTitle(value);
          clearError("title");
        }}
        errorText={fieldErrors["title"]}
      />

      <Fieldset
        legend="Svaralternativer"
        helpText={`Mellom ${OPTIONS_MIN} og ${OPTIONS_MAX} alternativer.`}
        errorText={fieldErrors["options"]}
        className={styles.optionsFieldset}
      >
        <div className={styles.optionsList}>
          {options.map((option, index) => (
            <div className={styles.optionRow} key={index}>
              <TextInput
                className={styles.optionInput}
                label={`Alternativ ${index + 1}`}
                value={option}
                maxLength={OPTION_MAX}
                onChange={(_, value) => setOption(index, value)}
                errorText={fieldErrors[`options.${index}`]}
              />
              <Button
                type="button"
                variant="neutral-transparent"
                icon={<DeleteIcon />}
                iconVariant="only"
                disabled={options.length <= OPTIONS_MIN}
                onClick={() => removeOption(index)}
              >
                {`Fjern alternativ ${index + 1}`}
              </Button>
            </div>
          ))}
        </div>
        <div>
          <Button
            type="button"
            icon={<AddIcon />}
            iconVariant="left"
            disabled={options.length >= OPTIONS_MAX}
            onClick={addOption}
          >
            Legg til alternativ
          </Button>
        </div>
      </Fieldset>

      <RadioFieldset
        legend="Hvor mange svar kan hver deltaker gi?"
        name="mode"
        value={mode}
        onChange={handleMode}
      >
        <RadioInput value="single" label="Ett svar" />
        <RadioInput
          value="multiple"
          label="Flere svar – deltakeren kan krysse av flere alternativer"
        />
        <RadioInput
          value="dots"
          label="Dot voting – deltakeren fordeler et antall stemmer"
        />
      </RadioFieldset>

      {mode === "dots" && (
        <NumberInput
          label={`Stemmer per deltaker (${DOTS_MIN}–${DOTS_MAX})`}
          helpText="Stemmene kan fordeles fritt, også flere på samme alternativ."
          value={maxDots}
          min={DOTS_MIN}
          max={DOTS_MAX}
          onChange={(_, value) => {
            setMaxDots(value);
            clearError("maxDots");
          }}
          errorText={fieldErrors["maxDots"]}
        />
      )}

      <ButtonGroup variant="left">
        <Button type="submit" variant="strong" disabled={busy}>
          {busy ? "Oppretter …" : "Opprett avstemning"}
        </Button>
      </ButtonGroup>
    </form>
  );
}
